import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const MARKETING_TEMPLATES = {
  provider: [
    {
      id: 'provider-onboarding-reminder',
      label: 'Onboarding Reminder',
      description: 'Remind providers to complete onboarding and start receiving jobs.',
      emailSubject: 'Action needed: complete onboarding, {{firstName}}',
      emailBody:
        '<p>Hi {{name}},</p><p>Your provider account is almost ready. Please complete onboarding to start receiving new leads from {{companyName}}.</p><p>Need help? Reply to this email and our team will assist you.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Complete your onboarding',
      pushBody:
        'Hi {{firstName}}, finish onboarding today to unlock new lead opportunities on {{companyName}}.',
    },
    {
      id: 'provider-promo-offer',
      label: 'Promotional Offer',
      description: 'Share campaign offers with selected service providers.',
      emailSubject: 'Exclusive provider promotion for {{firstName}}',
      emailBody:
        '<p>Hi {{name}},</p><p>We have an exclusive promotion for selected service providers this week.</p><p>Check your dashboard for details and activate the offer before it expires.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Exclusive provider offer',
      pushBody: 'Hi {{firstName}}, your exclusive provider offer is now live. Check your dashboard now.',
    },
    {
      id: 'provider-important-update',
      label: 'Important Update',
      description: 'Send operational or policy updates to providers.',
      emailSubject: 'Important update for service providers',
      emailBody:
        '<p>Hi {{name}},</p><p>We have an important update regarding your {{audienceLabel}} account.</p><p>Please review your dashboard notifications for full details.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Important account update',
      pushBody: 'Hi {{firstName}}, please review an important update in your app notifications.',
    },
  ],
  customer: [
    {
      id: 'customer-onboarding-reminder',
      label: 'Onboarding Reminder',
      description: 'Encourage customers to complete account setup.',
      emailSubject: 'Welcome {{firstName}} - complete your setup',
      emailBody:
        '<p>Hi {{name}},</p><p>Welcome to {{companyName}}. Complete your profile to enjoy a smoother booking experience.</p><p>If you need help, simply reply to this email.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Welcome to {{companyName}}',
      pushBody: 'Hi {{firstName}}, complete your setup to start booking trusted services quickly.',
    },
    {
      id: 'customer-promo-offer',
      label: 'Promotional Offer',
      description: 'Share selected offers and promotions with customers.',
      emailSubject: 'Special offer just for you, {{firstName}}',
      emailBody:
        '<p>Hi {{name}},</p><p>We have a special promotion available for selected customers.</p><p>Open the app now to explore available offers and save on your next booking.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Your special offer is live',
      pushBody: 'Hi {{firstName}}, unlock your latest promotion in the app today.',
    },
    {
      id: 'customer-important-update',
      label: 'Important Update',
      description: 'Send booking, support, or policy updates to customers.',
      emailSubject: 'Important update about your account',
      emailBody:
        '<p>Hi {{name}},</p><p>We are sharing an important update about your {{audienceLabel}} account.</p><p>Please check your notifications in the app for complete details.</p><p>Regards,<br/>{{companyName}} Team</p>',
      pushTitle: 'Important update',
      pushBody: 'Hi {{firstName}}, please review an important update from {{companyName}}.',
    },
  ],
};

const isProviderUserData = (userData = {}) => {
  const roles = userData.roles || {};
  return (
    roles.provider === true ||
    roles.provider === 'true' ||
    roles.client === true ||
    roles.client === 'true' ||
    userData.role === 'provider' ||
    userData.role === 'client'
  );
};

const isCustomerUserData = (userData = {}) => {
  const roles = userData.roles || {};
  return (
    roles.customer === true ||
    roles.customer === 'true' ||
    userData.role === 'customer'
  );
};

const normalizePhoneValue = (value) => {
  return String(value || '').replace(/[^0-9]/g, '');
};

const buildAudienceUserOption = (id, userData = {}) => {
  const displayName = String(
    userData.name ||
      userData.displayName ||
      userData.fullName ||
      userData.firstName ||
      userData.email ||
      id
  ).trim();
  const email = String(userData.email || '').trim().toLowerCase();
  const phone = String(
    userData.phone ||
      userData.phoneNumber ||
      userData.mobile ||
      userData.contactNumber ||
      ''
  ).trim();

  const searchLabel = [displayName, email, phone].filter(Boolean).join(' • ');

  return {
    id,
    displayName,
    email,
    phone,
    searchLabel,
  };
};

export default function Marketing() {
  const [audienceType, setAudienceType] = useState('provider');
  const [templateId, setTemplateId] = useState('provider-onboarding-reminder');
  const [audienceUsers, setAudienceUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoadError, setUsersLoadError] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendPush, setSendPush] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastResult, setLastResult] = useState(null);

  const templatesForAudience = useMemo(() => {
    return MARKETING_TEMPLATES[audienceType] || [];
  }, [audienceType]);

  const selectedTemplate = useMemo(() => {
    return templatesForAudience.find((template) => template.id === templateId) || templatesForAudience[0] || null;
  }, [templatesForAudience, templateId]);

  const selectedRecipientIds = useMemo(() => {
    return [...new Set(selectedRecipients.map((recipient) => String(recipient.id || '').trim()).filter(Boolean))];
  }, [selectedRecipients]);

  const selectedRecipientEmails = useMemo(() => {
    return [...new Set(selectedRecipients.map((recipient) => String(recipient.email || '').trim().toLowerCase()).filter(Boolean))];
  }, [selectedRecipients]);

  const selectedRecipientPhones = useMemo(() => {
    return [...new Set(selectedRecipients.map((recipient) => normalizePhoneValue(recipient.phone)).filter((phone) => phone.length >= 6))];
  }, [selectedRecipients]);

  useEffect(() => {
    let isMounted = true;

    const loadAudienceUsers = async () => {
      setUsersLoading(true);
      setUsersLoadError('');

      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const nextAudienceUsers = usersSnapshot.docs
          .map((docSnapshot) => ({
            id: docSnapshot.id,
            data: docSnapshot.data() || {},
          }))
          .filter(({ data }) => {
            return audienceType === 'provider'
              ? isProviderUserData(data)
              : isCustomerUserData(data);
          })
          .map(({ id, data }) => buildAudienceUserOption(id, data))
          .sort((left, right) => {
            return left.displayName.toLowerCase().localeCompare(right.displayName.toLowerCase());
          });

        if (!isMounted) {
          return;
        }

        setAudienceUsers(nextAudienceUsers);
        setSelectedRecipients((previous) => {
          if (previous.length === 0) {
            return previous;
          }

          const availableIds = new Set(nextAudienceUsers.map((user) => user.id));
          return previous.filter((recipient) => availableIds.has(recipient.id));
        });
      } catch (error) {
        console.error('Failed to load audience users for marketing:', error);
        if (isMounted) {
          setUsersLoadError('Failed to load recipient list. Please refresh and try again.');
          setAudienceUsers([]);
        }
      } finally {
        if (isMounted) {
          setUsersLoading(false);
        }
      }
    };

    loadAudienceUsers();

    return () => {
      isMounted = false;
    };
  }, [audienceType]);

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    if (selectedTemplate.id !== templateId) {
      setTemplateId(selectedTemplate.id);
      return;
    }

    setEmailSubject(selectedTemplate.emailSubject);
    setEmailBody(selectedTemplate.emailBody);
    setPushTitle(selectedTemplate.pushTitle);
    setPushBody(selectedTemplate.pushBody);
  }, [selectedTemplate, templateId]);

  const handleSendCampaign = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setLastResult(null);

    if (!sendEmail && !sendPush) {
      setErrorMessage('Select at least one channel: Email or Push Notification.');
      return;
    }

    if (selectedRecipientIds.length === 0) {
      setErrorMessage('Select at least one recipient from the list.');
      return;
    }

    if (sendEmail && (!emailSubject.trim() || !emailBody.trim())) {
      setErrorMessage('Email subject and email body are required when Email is enabled.');
      return;
    }

    if (sendPush && (!pushTitle.trim() || !pushBody.trim())) {
      setErrorMessage('Push title and push body are required when Push is enabled.');
      return;
    }

    setSending(true);
    try {
      const sendTargetedMarketingCommunication = httpsCallable(
        functions,
        'sendTargetedMarketingCommunication'
      );

      const response = await sendTargetedMarketingCommunication({
        audienceType,
        templateId: selectedTemplate?.id || 'custom',
        templateName: selectedTemplate?.label || 'Custom',
        recipientUserIds: selectedRecipientIds,
        recipientEmails: selectedRecipientEmails,
        recipientPhones: selectedRecipientPhones,
        sendEmail,
        sendPush,
        emailSubject,
        emailBody,
        pushTitle,
        pushBody,
      });

      const resultData = response?.data || {};
      setLastResult(resultData);
      setSuccessMessage('Campaign sent successfully. Email and push processing is complete.');
    } catch (error) {
      console.error('Failed to send targeted marketing communication:', error);
      setErrorMessage(error?.message || 'Failed to send campaign. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Marketing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send targeted and personalised campaigns to Service Providers or Customers using Email and Push Notification only.
      </Typography>

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Audience and Recipients
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="audience-type-label">Target Audience</InputLabel>
              <Select
                labelId="audience-type-label"
                value={audienceType}
                label="Target Audience"
                onChange={(event) => {
                  setAudienceType(event.target.value);
                  setTemplateId('');
                  setSelectedRecipients([]);
                }}
              >
                <MenuItem value="provider">Service Provider</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="template-label">Template</InputLabel>
              <Select
                labelId="template-label"
                value={selectedTemplate?.id || ''}
                label="Template"
                onChange={(event) => setTemplateId(event.target.value)}
              >
                {templatesForAudience.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{selectedTemplate?.description || ''}</FormHelperText>
            </FormControl>

            {usersLoadError ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {usersLoadError}
              </Alert>
            ) : null}

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={audienceUsers}
              value={selectedRecipients}
              onChange={(event, nextSelection) => setSelectedRecipients(nextSelection)}
              loading={usersLoading}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.searchLabel || option.displayName || option.id}
              renderOption={(props, option, state) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox checked={state.selected} sx={{ p: 0.5 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email || 'No email'}{option.phone ? ` • ${option.phone}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Recipients"
                  placeholder="Search by name, email, or contact"
                  helperText="Search and select one or more recipients from the list"
                  sx={{ mb: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`Selected: ${selectedRecipientIds.length}`} size="small" />
              <Chip label={`Selected emails: ${selectedRecipientEmails.length}`} size="small" />
              <Chip label={`Selected contacts: ${selectedRecipientPhones.length}`} size="small" />
            </Stack>

            <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Channels
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={(event) => setSendEmail(event.target.checked)}
                  />
                }
                label="Email"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendPush}
                    onChange={(event) => setSendPush(event.target.checked)}
                  />
                }
                label="Push Notification"
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSendCampaign}
              disabled={sending}
            >
              {sending ? 'Sending Campaign...' : 'Send Targeted Campaign'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Auto-Generated Content from Template
            </Typography>

            <TextField
              label="Email Subject"
              fullWidth
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
              sx={{ mb: 2 }}
              disabled={!sendEmail}
            />

            <TextField
              label="Email Body (HTML supported)"
              fullWidth
              multiline
              minRows={7}
              value={emailBody}
              onChange={(event) => setEmailBody(event.target.value)}
              sx={{ mb: 2 }}
              disabled={!sendEmail}
            />

            <TextField
              label="Push Title"
              fullWidth
              value={pushTitle}
              onChange={(event) => setPushTitle(event.target.value)}
              sx={{ mb: 2 }}
              disabled={!sendPush}
            />

            <TextField
              label="Push Body"
              fullWidth
              multiline
              minRows={3}
              value={pushBody}
              onChange={(event) => setPushBody(event.target.value)}
              disabled={!sendPush}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Available placeholders: {'{{name}}'}, {'{{firstName}}'}, {'{{audienceLabel}}'}, {'{{companyName}}'}
            </Typography>
          </Paper>

          {lastResult ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Last Campaign Result
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`Matched users: ${lastResult.matchedUsers || 0}`} color="primary" />
                <Chip label={`Email sent: ${lastResult.email?.directSent || 0}`} color="success" />
                <Chip label={`Email queued: ${lastResult.email?.queued || 0}`} />
                <Chip label={`Push sent: ${lastResult.push?.sent || 0}`} color="success" />
                <Chip label={`Push failed: ${lastResult.push?.failed || 0}`} color="warning" />
              </Stack>

              {Array.isArray(lastResult.unmatchedEmails) && lastResult.unmatchedEmails.length > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Unmatched Emails: {lastResult.unmatchedEmails.join(', ')}
                </Typography>
              ) : null}

              {Array.isArray(lastResult.unmatchedPhones) && lastResult.unmatchedPhones.length > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Unmatched Contacts: {lastResult.unmatchedPhones.join(', ')}
                </Typography>
              ) : null}

              {Array.isArray(lastResult.push?.missingUsers) && lastResult.push.missingUsers.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Matched users without FCM token: {lastResult.push.missingUsers.length}
                </Typography>
              ) : null}
            </Paper>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
}
